// Iter 279 — Pending Parcels Strip
// --------------------------------------------------------------
// Thin status row shown on each document-tool panel (Packing List,
// Bulk Data, Pack Team Print, Bulk Label) that answers the user's
// real-time question: "how many parcels from today's completed
// invoices still need THIS tool's file generated?"
//
// Formula:
//    total   = Σ parcel_count across invoices completed today
//    done    = Σ parcel_count where uses_tool[tool] ≥ 1
//    pending = total − done
//
// Data source: /api/order-register/completed-today (same endpoint
// the Completed Invoices chip bar already uses). Polled every 15s
// so the number updates without a page refresh.
// --------------------------------------------------------------
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { useWorkingWindowLabel } from "@/hooks/useWorkingWindow";
import { Package, PackageCheck, PackageMinus } from "lucide-react";

const TOOL_LABELS = {
  bulk_data: "Bulk Data",
  packing_list: "Packing List",
  pack_team: "Pack Team Print",
  bulk_label: "Bulk Label",
};

// Iter 290 — same scope vocabulary as the Completed Invoices bar, shared
// via the same localStorage key + window event so both stay in lockstep.
const SCOPE_OPTIONS = [
  { id: "today", label: "Today" },
  { id: "today_yesterday", label: "Today + Yesterday" },
  { id: "all", label: "All Time" },
];

const PendingParcelsStrip = ({ tool, className = "" }) => {
  const [stats, setStats] = useState(null);
  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem("ci-scope") || "today_yesterday"; }
    catch { return "today_yesterday"; }
  });
  const ww = useWorkingWindowLabel();
  const OPTS = SCOPE_OPTIONS.map(o => (o.id === "today_yesterday" && ww?.label ? { ...o, label: ww.label } : o));
  const scopeLabel = OPTS.find(o => o.id === scope)?.label || scope;

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/order-register/completed-today`,
        { headers: getAuthHeader(),
          params: { scope, tool: tool || "" } });
      const invoices = r.data?.invoices || [];
      let total = 0, done = 0, invTotal = 0, invDone = 0;
      for (const inv of invoices) {
        const n = Number(inv.parcel_count || 0);
        total += n;
        invTotal += 1;
        const uses = Number(inv.uses_tool?.[tool] || 0);
        if (uses > 0) {
          done += n;
          invDone += 1;
        }
      }
      setStats({ total, done, pending: Math.max(0, total - done), invTotal, invDone });
    } catch { /* keep previous value on transient errors */ }
  }, [tool, scope]);

  // Follow scope changes made in the Completed Invoices bar (and tell the
  // bar when the strip's own switcher changes it).
  useEffect(() => {
    const onScope = (ev) => {
      if (ev?.detail && SCOPE_OPTIONS.some(o => o.id === ev.detail)) {
        setScope(ev.detail);
      }
    };
    window.addEventListener("ci-scope-changed", onScope);
    return () => window.removeEventListener("ci-scope-changed", onScope);
  }, []);

  const setScopeShared = (v) => {
    setScope(v);
    try { localStorage.setItem("ci-scope", v); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("ci-scope-changed", { detail: v }));
  };

  useEffect(() => {
    fetchStats();
    // Light polling — matches CompletedInvoicesBar cadence so counters
    // stay in sync when ops marks a new invoice completed.
    const id = setInterval(() => { if (!document.hidden) fetchStats(); }, 15000);
    const onInvChange = () => fetchStats();
    window.addEventListener("order-register:invoice-changed", onInvChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("order-register:invoice-changed", onInvChange);
    };
  }, [fetchStats]);

  if (!stats) return null;
  const { total, done, pending, invTotal, invDone } = stats;
  const label = TOOL_LABELS[tool] || tool;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div
      data-testid={`pending-strip-${tool}`}
      className={`mb-3 rounded-lg border bg-gradient-to-r from-slate-50 to-white px-3 py-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs ${className}`}
    >
      <div className="flex items-center gap-1.5 text-slate-700" data-testid={`pending-strip-${tool}-label`}>
        <Package className="w-3.5 h-3.5 text-slate-500" />
        <span className="font-semibold">{label} — {scopeLabel} Progress</span>
        {/* Iter 290 — same window switcher as the Completed Invoices bar;
            both stay in lockstep via the shared ci-scope key + event */}
        <span className="inline-flex items-center rounded-full bg-white border border-slate-200 overflow-hidden ml-1"
              data-testid={`pending-strip-${tool}-scope`}>
          {OPTS.map((o) => (
            <button key={o.id} type="button"
                    onClick={() => setScopeShared(o.id)}
                    data-testid={`pending-strip-${tool}-scope-${o.id}`}
                    className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      scope === o.id
                        ? "bg-slate-700 text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}>
              {o.label}
            </button>
          ))}
        </span>
      </div>
      <div className="flex items-center gap-1 text-slate-600">
        <span>Completed Invoices:</span>
        <span className="font-semibold text-slate-800" data-testid={`pending-strip-${tool}-total`}>
          {total}
        </span>
        <span className="text-slate-400">parcels</span>
        <span className="text-[10px] text-slate-400">({invTotal} inv)</span>
      </div>
      <div className="flex items-center gap-1 text-emerald-700">
        <PackageCheck className="w-3.5 h-3.5" />
        <span>Generated:</span>
        <span className="font-semibold" data-testid={`pending-strip-${tool}-done`}>{done}</span>
        <span className="text-[10px] text-emerald-500">({invDone} inv)</span>
      </div>
      <button
        type="button"
        onClick={() => {
          if (pending > 0) {
            window.dispatchEvent(new CustomEvent(
              "completed-invoices:pending-filter",
              { detail: { tool } }
            ));
          }
        }}
        disabled={pending === 0}
        className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 transition-colors ${
          pending > 0
            ? "text-amber-700 hover:bg-amber-100 cursor-pointer"
            : "text-emerald-700 cursor-default"
        }`}
        title={pending > 0 ? "Click to filter the Completed Invoices bar to pending-only" : "All invoices done"}
        data-testid={`pending-strip-${tool}-pending-btn`}
      >
        <PackageMinus className="w-3.5 h-3.5" />
        <span>Pending:</span>
        <span
          className={`font-bold ${pending > 0 ? "text-amber-800 underline underline-offset-2" : "text-emerald-800"}`}
          data-testid={`pending-strip-${tool}-pending`}
        >
          {pending}
        </span>
        <span className="text-[10px]">
          {pending === 0 && total > 0 ? "✓ all done" : `parcels`}
        </span>
      </button>
      {total > 0 && (
        <div className="flex-1 min-w-[120px] max-w-[260px] flex items-center gap-2">
          <div className="h-1.5 flex-1 rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-full ${pending === 0 ? "bg-emerald-500" : "bg-amber-500"} transition-all`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[10px] text-slate-500 tabular-nums w-8 text-right">{pct}%</span>
        </div>
      )}
    </div>
  );
};

export default PendingParcelsStrip;
